/**
 * Data-driven column config for the OPTA stats page.
 *
 * Each `StatTypeConfig` maps a stat-type token (`attack`, `shooting`, ...)
 * onto:
 *   - `fields`: the comma-separated `fields` query the ffh-backend stats
 *     endpoints expect.
 *   - `groups`: ordered column groups rendered left-to-right. Each group
 *     becomes a `<th>` in the main header row (with `colspan = columns.length`),
 *     and each column becomes a `<th>` in the sub-header row plus a `<td>` in
 *     each body row. The cell value is read from `row` by `path` (dot-notation
 *     into the nested ffh-backend response).
 *
 * Generic `TableHeaders` and `TableBody` iterate over these configs; there
 * is no per-stat-type JSX branching.
 */

export type ColumnFormat = 'integer' | 'number' | 'percent' | 'price';

export type StatColumn = {
  /** Sub-header label (short — e.g. `OT`). */
  label: string;
  /** Tooltip on hover. */
  title?: string;
  /** Dot-path into the nested response, e.g. `shooting.shots.onTarget`. */
  path: string;
  /** Sort key sent to the API. Defaults to `path`. */
  sortKey?: string;
  /** Cell formatter — defaults to `number`. */
  format?: ColumnFormat;
  /** Drop the column on narrow viewports. */
  hideOnMobile?: boolean;
};

export type StatColumnGroup = {
  /** Main-header label (e.g. `Goal Threat`). Use an empty string for no group label. */
  label: string;
  /** Drop the entire group on narrow viewports. */
  hideOnMobile?: boolean;
  columns: StatColumn[];
};

export type StatTypeConfig = {
  fields: string;
  groups: StatColumnGroup[];
};

export type StatTypeMap = Record<string, StatTypeConfig>;

const goalThreatColumns: StatColumn[] = [
  { label: 'S', title: 'Shots', path: 'shooting.shots.total' },
  { label: 'OT', title: 'Shots on target', path: 'shooting.shots.onTarget' },
  { label: 'In', title: 'Shots in box', path: 'shooting.shots.inBox' },
  { label: 'BC', title: 'Big chances', path: 'shooting.bigChances' },
  {
    label: 'xG',
    title: 'Expected goals',
    path: 'shooting.expectedGoals',
    format: 'number'
  },
  { label: 'G', title: 'Goals', path: 'shooting.goals.total' }
];

const shootingAttemptColumns: StatColumn[] = [
  { label: 'S', title: 'Shots', path: 'shooting.shots.total' },
  {
    label: 'OT',
    title: 'Shots on target',
    path: 'shooting.shots.onTarget'
  },
  { label: 'In', title: 'Shots in box', path: 'shooting.shots.inBox' },
  {
    label: 'Out',
    title: 'Shots outside box',
    path: 'shooting.shots.outsideBox'
  },
  {
    label: 'Hd',
    title: 'Headed attempts',
    path: 'shooting.shots.headed'
  },
  { label: 'BC', title: 'Big chances', path: 'shooting.bigChances' },
  {
    label: 'NPxG',
    title: 'Non-penalty expected goals',
    path: 'shooting.nonPenaltyExpectedGoals',
    format: 'number',
    hideOnMobile: true
  },
  {
    label: 'xG',
    title: 'Expected goals',
    path: 'shooting.expectedGoals',
    format: 'number'
  }
];

const goalInvolvementColumns: StatColumn[] = [
  {
    label: 'xGI',
    title: 'Expected goal involvements (xG + xA)',
    path: 'attack.expectedGoalInvolvements',
    format: 'number'
  },
  {
    label: 'npxGI',
    title: 'Non-penalty expected goal involvements (npxG + xA)',
    path: 'attack.nonPenaltyExpectedGoalInvolvements',
    format: 'number'
  },
  {
    label: 'GI',
    title: 'Goal involvements (goals + assists)',
    path: 'attack.goalInvolvements'
  }
];

const playerCreativityColumns: StatColumn[] = [
  { label: 'KP', title: 'Key passes', path: 'creativity.keyPasses' },
  {
    label: 'BCC',
    title: 'Big chances created',
    path: 'creativity.bigChancesCreated'
  },
  {
    label: 'xA',
    title: 'Expected assists',
    path: 'creativity.expectedAssists',
    format: 'number'
  },
  { label: 'A', title: 'Assists', path: 'creativity.assists' }
];

const playerFplCoreColumns: StatColumn[] = [
  {
    label: 'xPts',
    title: 'Expected points',
    path: 'fpl.expectedPoints',
    format: 'number'
  },
  {
    label: 'BPS',
    title: 'Bonus points system score',
    path: 'fpl.bps',
    hideOnMobile: true
  },
  { label: 'B', title: 'Bonus points', path: 'fpl.bonus', hideOnMobile: true },
  {
    label: 'DC',
    title: 'Defensive contribution points',
    path: 'fpl.defensiveContribution.points'
  },
  { label: 'Pts', title: 'FPL points', path: 'fpl.points' }
];

export const PLAYER_STAT_TYPES: StatTypeMap = {
  attack: {
    fields: 'attack,shooting,creativity,fpl',
    groups: [
      { label: 'Goal threat', columns: goalThreatColumns },
      {
        label: 'Goal involvement',
        hideOnMobile: true,
        columns: goalInvolvementColumns
      },
      { label: 'Creativity', columns: playerCreativityColumns },
      { label: 'FPL', columns: playerFplCoreColumns }
    ]
  },
  shooting: {
    fields: 'shooting',
    groups: [
      { label: 'Attempts', columns: shootingAttemptColumns },
      {
        label: 'Goals',
        columns: [
          { label: 'G', title: 'Goals', path: 'shooting.goals.total' },
          { label: 'In', title: 'Goals in box', path: 'shooting.goals.inBox' },
          {
            label: 'Out',
            title: 'Goals outside box',
            path: 'shooting.goals.outsideBox'
          },
          { label: 'Hd', title: 'Headed goals', path: 'shooting.goals.headed' }
        ]
      }
    ]
  },
  creativity: {
    fields: 'creativity',
    groups: [
      {
        label: 'Chances created',
        columns: [
          { label: 'KP', title: 'Key passes', path: 'creativity.keyPasses' },
          {
            label: 'BCC',
            title: 'Big chances created',
            path: 'creativity.bigChancesCreated'
          },
          {
            label: 'xA',
            title: 'Expected assists',
            path: 'creativity.expectedAssists',
            format: 'number'
          },
          { label: 'A', title: 'Assists', path: 'creativity.assists' }
        ]
      },
      {
        label: 'Crosses',
        columns: [
          {
            label: 'Tot.',
            title: 'Total crosses',
            path: 'creativity.crosses.total'
          },
          {
            label: 'Succ.',
            title: 'Successful crosses',
            path: 'creativity.crosses.successful'
          },
          {
            label: '%',
            title: 'Cross completion',
            path: 'creativity.crosses.rate',
            format: 'percent'
          }
        ]
      }
    ]
  },
  passing: {
    fields: 'passing',
    groups: [
      {
        label: 'All passing',
        columns: [
          {
            label: 'Tot.',
            title: 'Total passes',
            path: 'passing.passes.total'
          },
          {
            label: 'Succ.',
            title: 'Accurate passes',
            path: 'passing.passes.accurate'
          },
          {
            label: '%',
            title: 'Pass completion',
            path: 'passing.passes.rate',
            format: 'percent'
          }
        ]
      },
      {
        label: 'Opp. half',
        columns: [
          {
            label: 'Tot.',
            title: 'Total opposition-half passes',
            path: 'passing.oppositionHalf.total'
          },
          {
            label: 'Succ.',
            title: 'Accurate opposition-half passes',
            path: 'passing.oppositionHalf.accurate'
          },
          {
            label: '%',
            title: 'Opposition-half pass completion',
            path: 'passing.oppositionHalf.rate',
            format: 'percent'
          }
        ]
      },
      {
        label: 'Final 3rd',
        columns: [
          {
            label: 'Tot.',
            title: 'Total final-3rd passes',
            path: 'passing.finalThird.total'
          },
          {
            label: 'Succ.',
            title: 'Accurate final-3rd passes',
            path: 'passing.finalThird.accurate'
          },
          {
            label: '%',
            title: 'Final-3rd pass completion',
            path: 'passing.finalThird.rate',
            format: 'percent'
          }
        ]
      },
      {
        label: 'Through balls',
        columns: [
          {
            label: 'Tot.',
            title: 'Through balls attempted',
            path: 'passing.throughBalls.total'
          },
          {
            label: 'Succ.',
            title: 'Successful through balls',
            path: 'passing.throughBalls.accurate'
          }
        ]
      }
    ]
  },
  involvement: {
    fields: 'involvement',
    groups: [
      {
        label: 'Apps',
        columns: [
          {
            label: 'Starts',
            title: 'Games started',
            path: 'participation.starts'
          },
          {
            label: 'Sub on',
            title: 'Appearances as a substitute',
            path: 'participation.substitutions.on'
          },
          {
            label: 'Sub off',
            title: 'Times substituted off',
            path: 'participation.substitutions.off'
          }
        ]
      },
      {
        label: 'Touches',
        columns: [
          {
            label: 'All',
            title: 'Total touches',
            path: 'involvement.touches.total'
          },
          {
            label: 'Box',
            title: 'Touches in the opposition box',
            path: 'involvement.touches.oppositionBox'
          }
        ]
      },
      {
        label: 'Entries',
        columns: [
          {
            label: 'Final 3rd',
            title: 'Final-third entries',
            path: 'involvement.finalThirdEntries'
          },
          {
            label: 'Box',
            title: 'Penalty-area entries',
            path: 'involvement.penaltyAreaEntries'
          }
        ]
      },
      {
        label: 'Fouls',
        columns: [
          {
            label: 'Won F3',
            title: 'Fouls won in final third',
            path: 'involvement.fouls.wonFinalThird'
          },
          {
            label: 'Pen',
            title: 'Penalties won',
            path: 'involvement.penaltiesWon'
          }
        ]
      }
    ]
  },
  setpieces: {
    fields: 'setPieces',
    groups: [
      {
        label: 'Set pieces',
        columns: [
          {
            label: 'C',
            title: 'Corners taken',
            path: 'setPieces.corners.taken'
          },
          {
            label: 'Pen',
            title: 'Penalties taken',
            path: 'setPieces.penalties.taken'
          }
        ]
      },
      {
        label: 'Free kicks',
        columns: [
          {
            label: 'Cross',
            title: 'Free kick crosses into box',
            path: 'setPieces.freeKicks.total'
          },
          {
            label: 'Shot',
            title: 'Direct free kick shots',
            path: 'setPieces.freeKicks.direct'
          },
          {
            label: 'G',
            title: 'Direct free kick goals',
            path: 'setPieces.freeKicks.scored'
          }
        ]
      }
    ]
  },
  defending: {
    fields: 'defending,defensiveActions',
    groups: [
      {
        label: 'Opposition output',
        columns: [
          { label: 'CS', title: 'Clean sheets', path: 'defending.cleanSheets' },
          {
            label: 'GC',
            title: 'Goals conceded',
            path: 'defending.goalsConceded'
          },
          {
            label: 'xGC',
            title: 'Expected goals conceded',
            path: 'defending.expectedGoalsConceded',
            format: 'number'
          },
          {
            label: 'xCS',
            title: 'Expected clean sheets',
            path: 'defending.expectedCleanSheets',
            format: 'number'
          }
        ]
      },
      {
        label: 'Tackles',
        columns: [
          {
            label: 'Tot.',
            title: 'Total tackles',
            path: 'defensiveActions.tackles.total'
          },
          {
            label: 'Won',
            title: 'Tackles won',
            path: 'defensiveActions.tackles.won'
          },
          {
            label: '%',
            title: 'Tackles won %',
            path: 'defensiveActions.tackles.rate',
            format: 'percent'
          }
        ]
      },
      {
        label: 'CBIT',
        columns: [
          {
            label: 'Rec',
            title: 'Ball recoveries',
            path: 'defensiveActions.recoveries'
          },
          {
            label: 'Clr',
            title: 'Clearances',
            path: 'defensiveActions.clearances'
          },
          { label: 'Blk', title: 'Blocks', path: 'defensiveActions.blocks' },
          {
            label: 'Int',
            title: 'Interceptions',
            path: 'defensiveActions.interceptions'
          },
          {
            label: 'CBIT',
            title:
              'Clearances, blocks, interceptions and tackles (DefCon tally for defenders)',
            path: 'defensiveActions.cbit'
          },
          {
            label: 'CBITR',
            title:
              'Clearances, blocks, interceptions, tackles and recoveries (DefCon tally for midfielders and forwards)',
            path: 'defensiveActions.cbitr'
          }
        ]
      }
    ]
  },
  defensive: {
    fields: 'defending',
    groups: [
      {
        label: 'Opposition output',
        columns: [
          {
            label: 'BCC',
            title: 'Big chances conceded',
            path: 'defending.bigChancesConceded'
          },
          {
            label: 'SC',
            title: 'Shots conceded',
            path: 'defending.shotsConceded.total'
          },
          {
            label: 'xGC',
            title: 'Expected goals conceded',
            path: 'defending.expectedGoalsConceded',
            format: 'number'
          },
          {
            label: 'xCS',
            title: 'Expected clean sheets',
            path: 'defending.expectedCleanSheets',
            format: 'number'
          },
          {
            label: 'GC',
            title: 'Goals conceded',
            path: 'defending.goalsConceded'
          },
          { label: 'CS', title: 'Clean sheets', path: 'defending.cleanSheets' }
        ]
      }
    ]
  },
  fpl: {
    fields: 'fpl',
    groups: [
      {
        label: 'FPL bonus',
        columns: [
          { label: 'BPS', title: 'Bonus points system score', path: 'fpl.bps' },
          { label: 'B', title: 'Bonus points', path: 'fpl.bonus' },
          {
            label: 'bBPS',
            title: 'Baseline BPS',
            path: 'fpl.baselineBps',
            hideOnMobile: true
          }
        ]
      },
      {
        label: 'Indices',
        columns: [
          {
            label: 'Threat',
            title: 'Threat index',
            path: 'fpl.threat',
            format: 'number'
          },
          {
            label: 'Creat.',
            title: 'Creativity index',
            path: 'fpl.creativity',
            format: 'number',
            hideOnMobile: true
          },
          {
            label: 'Inf',
            title: 'Influence index',
            path: 'fpl.influence',
            format: 'number',
            hideOnMobile: true
          }
        ]
      },
      {
        label: 'Expected',
        columns: [
          {
            label: 'xPts',
            title: 'Expected FPL points',
            path: 'fpl.expectedPoints',
            format: 'number'
          },
          {
            label: 'xAtt',
            title: 'Expected attacking points',
            path: 'fpl.expectedAttackingPoints',
            format: 'number'
          }
        ]
      },
      {
        label: 'Discipline',
        columns: [
          { label: 'SV', title: 'Saves', path: 'fpl.saves' },
          { label: 'YC', title: 'Yellow cards', path: 'fpl.yellowCards' },
          {
            label: 'RC',
            title: 'Red cards',
            path: 'fpl.redCards',
            hideOnMobile: true
          }
        ]
      },
      {
        label: 'Total',
        columns: [
          {
            label: 'aPts',
            title: 'Adjusted points (FPL minus appearance points)',
            path: 'fpl.adjustedPoints',
            hideOnMobile: true
          },
          {
            label: 'xDC',
            title: 'Expected defensive contribution points',
            path: 'fpl.defensiveContribution.expectedPoints',
            format: 'number',
            hideOnMobile: true
          },
          {
            label: 'DC',
            title: 'Defensive contribution points',
            path: 'fpl.defensiveContribution.points'
          },
          { label: 'Pts', title: 'FPL points', path: 'fpl.points' }
        ]
      }
    ]
  }
};

const teamCreativityColumns: StatColumn[] = [
  { label: 'KP', title: 'Key passes', path: 'creativity.keyPasses' },
  {
    label: 'BCC',
    title: 'Big chances created',
    path: 'creativity.bigChancesCreated'
  },
  {
    label: 'xA',
    title: 'Expected assists',
    path: 'creativity.expectedAssists',
    format: 'number'
  },
  { label: 'A', title: 'Assists', path: 'creativity.assists' }
];

export const TEAM_STAT_TYPES: StatTypeMap = {
  attack: {
    fields: 'shooting,creativity,defending,fpl',
    groups: [
      { label: 'Goal threat', columns: goalThreatColumns },
      {
        label: 'Defensive',
        columns: [
          {
            label: 'xGC',
            title: 'Expected goals conceded',
            path: 'defending.expectedGoalsConceded',
            format: 'number'
          },
          {
            label: 'xCS',
            title: 'Expected clean sheets',
            path: 'defending.expectedCleanSheets',
            format: 'number'
          },
          {
            label: 'GC',
            title: 'Goals conceded',
            path: 'defending.goalsConceded'
          },
          { label: 'CS', title: 'Clean sheets', path: 'defending.cleanSheets' }
        ]
      },
      {
        label: 'Overall',
        columns: [
          {
            label: 'xGD',
            title: 'Expected goal difference',
            path: 'defending.expectedGoalDifference',
            format: 'number'
          },
          {
            label: 'GD',
            title: 'Goal difference',
            path: 'defending.goalDifference'
          }
        ]
      },
      {
        label: 'FPL',
        columns: [{ label: 'Pts', title: 'FPL points', path: 'fpl.points' }]
      }
    ]
  },
  // Teams have no goals.inBox field, so the goals group differs from players.
  shooting: {
    fields: 'shooting',
    groups: [
      { label: 'Attempts', columns: shootingAttemptColumns },
      {
        label: 'Goals',
        columns: [
          { label: 'G', title: 'Goals', path: 'shooting.goals.total' },
          {
            label: 'Out',
            title: 'Goals outside box',
            path: 'shooting.goals.outsideBox'
          },
          { label: 'Hd', title: 'Headed goals', path: 'shooting.goals.headed' }
        ]
      }
    ]
  },
  creativity: {
    fields: 'creativity',
    groups: [
      { label: 'Chances created', columns: teamCreativityColumns },
      {
        label: 'Crosses',
        columns: [
          {
            label: 'Tot.',
            title: 'Total crosses',
            path: 'creativity.crosses.total'
          },
          {
            label: 'Succ.',
            title: 'Successful crosses',
            path: 'creativity.crosses.successful'
          },
          {
            label: '%',
            title: 'Cross completion',
            path: 'creativity.crosses.rate',
            format: 'percent'
          }
        ]
      },
      {
        label: 'Set pieces',
        columns: [
          {
            label: 'C',
            title: 'Corners taken',
            path: 'creativity.cornersTaken'
          },
          {
            label: 'FK',
            title: 'Free kicks into box',
            path: 'creativity.freeKicksIntoBox'
          }
        ]
      },
      {
        label: 'Created by position',
        hideOnMobile: true,
        columns: [
          {
            label: 'L%',
            title: 'Share of chances created from the left',
            path: 'creativity.chancesCreated.rate.left',
            format: 'percent'
          },
          {
            label: 'C%',
            title: 'Share of chances created centrally',
            path: 'creativity.chancesCreated.rate.center',
            format: 'percent'
          },
          {
            label: 'R%',
            title: 'Share of chances created from the right',
            path: 'creativity.chancesCreated.rate.right',
            format: 'percent'
          },
          {
            label: 'L',
            title: 'Chances created from the left',
            path: 'creativity.chancesCreated.left'
          },
          {
            label: 'C',
            title: 'Chances created centrally',
            path: 'creativity.chancesCreated.center'
          },
          {
            label: 'R',
            title: 'Chances created from the right',
            path: 'creativity.chancesCreated.right'
          }
        ]
      }
    ]
  },
  passing: PLAYER_STAT_TYPES.passing,
  involvement: {
    fields: 'involvement',
    groups: [
      {
        label: 'Touches',
        columns: [
          {
            label: 'All',
            title: 'Total touches',
            path: 'involvement.touches.total'
          },
          {
            label: 'Box',
            title: 'Touches in the opposition box',
            path: 'involvement.touches.oppositionBox'
          }
        ]
      },
      {
        label: 'Entries',
        columns: [
          {
            label: 'Final 3rd',
            title: 'Final-third entries',
            path: 'involvement.finalThirdEntries'
          },
          {
            label: 'Box',
            title: 'Penalty-area entries',
            path: 'involvement.penaltyAreaEntries'
          }
        ]
      },
      {
        label: 'Fouls',
        columns: [
          {
            label: 'Final 3rd',
            title: 'Fouls won in final third',
            path: 'involvement.foulsWonFinalThird'
          },
          {
            label: 'Pen',
            title: 'Penalties won',
            path: 'involvement.penaltiesWon'
          }
        ]
      }
    ]
  },
  setpieces: {
    fields: 'setPieces',
    groups: [
      {
        label: 'Set pieces',
        columns: [
          {
            label: 'C',
            title: 'Corners taken',
            path: 'setPieces.corners.taken'
          },
          {
            label: 'Pen',
            title: 'Penalties taken',
            path: 'setPieces.penalties.taken'
          }
        ]
      },
      {
        label: 'Free kicks',
        columns: [
          {
            label: 'Cross',
            title: 'Free kick crosses into box',
            path: 'setPieces.freeKicks.intoBox'
          },
          {
            label: 'Shot',
            title: 'Direct free kick shots',
            path: 'setPieces.freeKicks.directAttempts'
          },
          {
            label: 'G',
            title: 'Direct free kick goals',
            path: 'setPieces.freeKicks.directGoals'
          }
        ]
      }
    ]
  },
  defending: {
    fields: 'defending,defensiveActions',
    groups: [
      {
        label: 'Opposition output',
        columns: [
          { label: 'CS', title: 'Clean sheets', path: 'defending.cleanSheets' },
          {
            label: 'GC',
            title: 'Goals conceded',
            path: 'defending.goalsConceded'
          },
          {
            label: 'xGC',
            title: 'Expected goals conceded',
            path: 'defending.expectedGoalsConceded',
            format: 'number'
          },
          {
            label: 'xCS',
            title: 'Expected clean sheets',
            path: 'defending.expectedCleanSheets',
            format: 'number'
          }
        ]
      },
      {
        label: 'Tackles',
        columns: [
          {
            label: 'Tot.',
            title: 'Total tackles',
            path: 'defensiveActions.tackles.total'
          },
          {
            label: 'Won',
            title: 'Tackles won',
            path: 'defensiveActions.tackles.won'
          },
          {
            label: '%',
            title: 'Tackles won %',
            path: 'defensiveActions.tackles.rate',
            format: 'percent'
          }
        ]
      },
      {
        label: 'CBIT',
        columns: [
          {
            label: 'Rec',
            title: 'Ball recoveries',
            path: 'defensiveActions.recoveries'
          },
          {
            label: 'Clr',
            title: 'Clearances',
            path: 'defensiveActions.clearances'
          },
          { label: 'Blk', title: 'Blocks', path: 'defensiveActions.blocks' },
          {
            label: 'Int',
            title: 'Interceptions',
            path: 'defensiveActions.interceptions'
          },
          {
            label: 'CBIT',
            title: 'Clearances, blocks, interceptions and tackles',
            path: 'defensiveActions.clearancesBlocksInterceptionsTackles'
          }
        ]
      }
    ]
  },
  defensive: {
    fields: 'defending',
    groups: [
      {
        label: 'Opposition output',
        columns: [
          {
            label: 'BCC',
            title: 'Big chances conceded',
            path: 'defending.bigChancesConceded'
          },
          {
            label: 'SC',
            title: 'Shots conceded',
            path: 'defending.shotsConceded.total'
          },
          {
            label: 'NPxGA',
            title: 'Non-penalty expected goals conceded',
            path: 'defending.nonPenaltyExpectedGoalsConceded',
            format: 'number',
            hideOnMobile: true
          },
          {
            label: 'xGC',
            title: 'Expected goals conceded',
            path: 'defending.expectedGoalsConceded',
            format: 'number'
          },
          {
            label: 'xCS',
            title: 'Expected clean sheets',
            path: 'defending.expectedCleanSheets',
            format: 'number'
          },
          {
            label: 'GC',
            title: 'Goals conceded',
            path: 'defending.goalsConceded'
          },
          { label: 'CS', title: 'Clean sheets', path: 'defending.cleanSheets' }
        ]
      },
      {
        label: 'Conceded by position',
        hideOnMobile: true,
        columns: [
          {
            label: 'L%',
            title: 'Share of chances conceded from the left',
            path: 'defending.chancesConceded.rate.left',
            format: 'percent'
          },
          {
            label: 'C%',
            title: 'Share of chances conceded centrally',
            path: 'defending.chancesConceded.rate.center',
            format: 'percent'
          },
          {
            label: 'R%',
            title: 'Share of chances conceded from the right',
            path: 'defending.chancesConceded.rate.right',
            format: 'percent'
          },
          {
            label: 'L',
            title: 'Chances conceded from the left',
            path: 'defending.chancesConceded.left'
          },
          {
            label: 'C',
            title: 'Chances conceded centrally',
            path: 'defending.chancesConceded.center'
          },
          {
            label: 'R',
            title: 'Chances conceded from the right',
            path: 'defending.chancesConceded.right'
          }
        ]
      }
    ]
  },
  fpl: {
    fields: 'fpl',
    groups: [
      {
        label: 'FPL bonus',
        columns: [
          { label: 'BPS', title: 'Bonus points system score', path: 'fpl.bps' },
          { label: 'B', title: 'Bonus points', path: 'fpl.bonus' }
        ]
      },
      {
        label: 'Indices',
        columns: [
          {
            label: 'Threat',
            title: 'Threat index',
            path: 'fpl.threat',
            format: 'number'
          },
          {
            label: 'Creat.',
            title: 'Creativity index',
            path: 'fpl.creativity',
            format: 'number'
          },
          {
            label: 'Inf',
            title: 'Influence index',
            path: 'fpl.influence',
            format: 'number'
          }
        ]
      },
      {
        label: 'Discipline',
        columns: [
          { label: 'YC', title: 'Yellow cards', path: 'fpl.yellowCards' },
          {
            label: 'RC',
            title: 'Red cards',
            path: 'fpl.redCards',
            hideOnMobile: true
          }
        ]
      },
      {
        label: 'Total',
        columns: [
          {
            label: 'aPts',
            title: 'Adjusted points (FPL minus appearance points)',
            path: 'fpl.adjustedPoints',
            hideOnMobile: true
          },
          { label: 'Pts', title: 'FPL points', path: 'fpl.points' }
        ]
      }
    ]
  }
};

export const getStatTypeConfig = (
  format: 'players' | 'teams',
  statType: string
): StatTypeConfig | undefined => {
  const map = format === 'teams' ? TEAM_STAT_TYPES : PLAYER_STAT_TYPES;
  return map[statType];
};

/**
 * First season for which detailed Opta event counts (shots, passing,
 * involvement, set pieces, defensive actions) exist in the data. Earlier seasons
 * only carry derived metrics (xG, xA, goals, assists) and FPL data, so the
 * count-family columns render as 0.00.
 *
 * See `ffh-public-api/docs/opta-stats-investigation.md` (DATA-1). Lower this
 * value if those earlier seasons are ever backfilled.
 */
export const OPTA_DETAILED_STATS_FROM_SEASON = 2025;

const FALLBACK_SORT_KEY = 'fpl.points';
const FPL_PATH_PREFIX = 'fpl.';

/**
 * Paths populated for every season (derived metrics, not raw Opta counts), so
 * they are safe to sort by even when detailed Opta data is missing. Any `fpl.*`
 * path is also populated and handled by the prefix check below.
 */
const HISTORICALLY_POPULATED_PATHS = new Set<string>([
  'shooting.expectedGoals',
  'shooting.nonPenaltyExpectedGoals',
  'shooting.goals.total',
  'creativity.expectedAssists',
  'creativity.assists',
  // Goal involvements derive from goals, assists, xG and xA, all of which
  // exist for every season.
  'attack.goalInvolvements',
  'attack.expectedGoalInvolvements',
  'attack.nonPenaltyExpectedGoalInvolvements'
]);

const columnSortKey = (column: StatColumn): string =>
  column.sortKey ?? column.path;

const isPopulatedForAllSeasons = (column: StatColumn): boolean => {
  const key = columnSortKey(column);
  return (
    key.startsWith(FPL_PATH_PREFIX) || HISTORICALLY_POPULATED_PATHS.has(key)
  );
};

/**
 * Default sort key for a stat-type, accounting for season data availability.
 *
 * For seasons with detailed Opta data this is the first column (current
 * behaviour). For earlier seasons it falls back to the first column in the
 * active tab that has data (xG, assists, FPL...), or FPL points when the tab is
 * entirely count-based. This stops the table defaulting to a sort by an empty
 * column.
 */
export const getDefaultSortKey = (
  config: StatTypeConfig | undefined,
  hasDetailedStats: boolean
): string => {
  const columns = config?.groups?.flatMap((group) => group.columns) ?? [];

  if (hasDetailedStats) {
    return columns[0] ? columnSortKey(columns[0]) : FALLBACK_SORT_KEY;
  }

  const populatedColumn = columns.find(isPopulatedForAllSeasons);
  return populatedColumn ? columnSortKey(populatedColumn) : FALLBACK_SORT_KEY;
};
